import { supabase } from "~/integrations/supabase/client";
import type {
  OilType,
  OilTypeFormData,
  OilTypePrice,
} from "~/types/purchasing";

export const oilTypeService = {
  async getAll(): Promise<OilType[]> {
    const { data, error } = await supabase
      .from("oil_types")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;
    return data as OilType[];
  },

  async getById(id: string): Promise<OilType> {
    const { data, error } = await supabase
      .from("oil_types")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as OilType;
  },

  async create(data: OilTypeFormData): Promise<OilType> {
    const { data: oilType, error } = await supabase
      .from("oil_types")
      .insert({
        ...data,
        density_kg_per_liter: data.density_kg_per_liter ?? 0.92,
      })
      .select()
      .single();

    if (error) throw error;
    return oilType as OilType;
  },

  async update(id: string, data: Partial<OilTypeFormData>): Promise<OilType> {
    const { data: oilType, error } = await supabase
      .from("oil_types")
      .update(data)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return oilType as OilType;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from("oil_types").delete().eq("id", id);
    if (error) throw error;
  },

  // PostgREST can't compare two columns in a filter, so this is client-side
  // (same approach as rawMaterialService.getLowStock).
  async getLowStock(): Promise<OilType[]> {
    const allData = await this.getAll();
    return allData.filter(
      (oil) =>
        oil.minimum_stock_liters !== null &&
        oil.current_stock_liters < oil.minimum_stock_liters
    );
  },

  // Price history methods
  async getPriceHistory(oilTypeId: string): Promise<OilTypePrice[]> {
    const { data, error } = await supabase
      .from("oil_type_prices")
      .select("*, supplier:suppliers(*)")
      .eq("oil_type_id", oilTypeId)
      .order("effective_date", { ascending: false });

    if (error) throw error;
    return data as OilTypePrice[];
  },

  async getLatestPrice(
    oilTypeId: string,
    supplierId?: string
  ): Promise<number | null> {
    const { data, error } = await supabase.rpc("get_latest_oil_price", {
      p_oil_type_id: oilTypeId,
      p_supplier_id: supplierId ?? null,
    });

    if (error) throw error;
    return data;
  },

  async addPrice(data: {
    oil_type_id: string;
    supplier_id?: string;
    unit_price: number;
    currency: "CLP" | "USD";
    effective_date: string;
  }): Promise<OilTypePrice> {
    const { data: price, error } = await supabase
      .from("oil_type_prices")
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return price as OilTypePrice;
  },
};
